import { useNavigate } from 'react-router-dom';
import { Banner, Button, BlockStack, Text, InlineStack, Card } from '@shopify/polaris';
import type { FeatureFlag } from '../lib/api';

interface UpgradePromptProps {
  feature: FeatureFlag;
  currentPlan?: string;
  variant?: 'banner' | 'card' | 'inline';
}

const FEATURE_NAMES: Record<FeatureFlag, string> = {
  standard_reports: 'Standard Reports',
  email_support: 'Email Support',
  ecommerce_reports: 'E-commerce Reports',
  realtime_analytics: 'Realtime Analytics',
  custom_events: 'Custom Events',
  priority_support: 'Priority Support',
  white_label: 'White Label',
  api_access: 'API Access',
  team_collaboration: 'Team Collaboration',
};

const FEATURE_PLANS: Record<FeatureFlag, string> = {
  standard_reports: 'Free',
  email_support: 'Free',
  ecommerce_reports: 'Starter',
  realtime_analytics: 'Pro',
  custom_events: 'Pro',
  priority_support: 'Pro',
  white_label: 'Agency',
  api_access: 'Agency',
  team_collaboration: 'Agency',
};

export function UpgradePrompt({ feature, currentPlan = 'Free', variant = 'banner' }: UpgradePromptProps) {
  const navigate = useNavigate();
  const featureName = FEATURE_NAMES[feature];
  const requiredPlan = FEATURE_PLANS[feature];

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  if (variant === 'banner') {
    return (
      <Banner tone="warning">
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            <strong>{featureName}</strong> is available on the {requiredPlan} plan and above.
            You're currently on the {currentPlan} plan.
          </Text>
          <InlineStack>
            <Button onClick={handleUpgrade}>Upgrade to {requiredPlan}</Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    );
  }

  if (variant === 'card') {
    return (
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd" fontWeight="bold">
              Unlock {featureName}
            </Text>
            <Text as="p" tone="subdued">
              This feature is available on the {requiredPlan} plan and above.
              Upgrade your subscription to access {featureName.toLowerCase()}.
            </Text>
          </BlockStack>
          <InlineStack>
            <Button variant="primary" onClick={handleUpgrade}>
              Upgrade to {requiredPlan}
            </Button>
            <Button onClick={() => navigate('/pricing')}>View All Plans</Button>
          </InlineStack>
        </BlockStack>
      </Card>
    );
  }

  // Inline variant
  return (
    <div
      style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <Text as="span" variant="bodySm">
        <strong>{featureName}</strong> requires {requiredPlan} plan
      </Text>
      <button
        onClick={handleUpgrade}
        style={{
          backgroundColor: '#2c6ecb',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '6px 12px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Upgrade
      </button>
    </div>
  );
}

interface SiteLimitPromptProps {
  sitesUsed: number;
  sitesLimit: number;
}

export function SiteLimitPrompt({ sitesUsed, sitesLimit }: SiteLimitPromptProps) {
  const navigate = useNavigate();

  if (sitesLimit === -1 || sitesUsed < sitesLimit) {
    return null;
  }

  return (
    <Banner tone="warning">
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          You've reached your site limit ({sitesUsed}/{sitesLimit} sites).
          Upgrade your plan to add more sites.
        </Text>
        <InlineStack>
          <Button onClick={() => navigate('/pricing')}>Upgrade Plan</Button>
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}

interface TrialBannerProps {
  daysRemaining: number;
  planName?: string;
}

export function TrialBanner({ daysRemaining, planName = 'Pro' }: TrialBannerProps) {
  const navigate = useNavigate();

  if (daysRemaining <= 0) {
    return null;
  }

  return (
    <Banner
      tone={daysRemaining <= 2 ? 'critical' : 'warning'}
      action={{
        content: 'Upgrade Now',
        onAction: () => navigate('/pricing'),
      }}
    >
      Your {planName} trial ends in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}.
      {daysRemaining <= 2 && ' Upgrade now to keep your features.'}
    </Banner>
  );
}
